import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  where 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaExclamationTriangle, FaGraduationCap, FaCalendar, FaClock, FaDollarSign, FaUser, FaEye } from 'react-icons/fa';
import { formatDate } from '../../utils/dateUtils';

const LearningSessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    userId: '',
    chatId: '',
    lesson_plan: '',
    summary: '',
    time_minutes: 0,
    attention_percentage: 0,
    cost_usd: 0,
    cost_per_minute_usd: 0,
    createdAt: new Date().toISOString().split('T')[0]
  });

  // Fetch sessions from Firestore
  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError('');
      const sessionsRef = collection(db, 'learning_sessions');
      const q = query(sessionsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const sessionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSessions(sessionsData);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      if (error.code === 'permission-denied') {
        setError('Permission denied. Please check your Firestore security rules.');
      } else if (error.code === 'unavailable') {
        setError('Firestore is currently unavailable. Please check your internet connection.');
      } else {
        setError(`Error loading sessions: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      userId: '',
      chatId: '',
      lesson_plan: '',
      summary: '',
      time_minutes: 0,
      attention_percentage: 0,
      cost_usd: 0,
      cost_per_minute_usd: 0,
      createdAt: new Date().toISOString().split('T')[0]
    });
    setEditingSession(null);
  };

  // Open modal for creating new session
  const handleCreateSession = () => {
    resetForm();
    setShowModal(true);
  };

  // Open modal for editing session
  const handleEditSession = (session) => {
    setEditingSession(session);
    setFormData({
      userId: session.userId || '',
      chatId: session.chatId || '',
      lesson_plan: session.lesson_plan || '',
      summary: session.summary || '',
      time_minutes: session.time_minutes || 0,
      attention_percentage: session.attention_percentage || 0,
      cost_usd: session.cost_usd || 0,
      cost_per_minute_usd: session.cost_per_minute_usd || 0,
      createdAt: session.createdAt || new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  // Save session (create or update)
  const handleSaveSession = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const sessionData = {
        ...formData,
        time_minutes: parseInt(formData.time_minutes) || 0,
        attention_percentage: parseFloat(formData.attention_percentage) || 0,
        cost_usd: parseFloat(formData.cost_usd) || 0,
        cost_per_minute_usd: parseFloat(formData.cost_per_minute_usd) || 0,
        updatedAt: new Date().toISOString()
      };

      if (editingSession) {
        // Update existing session
        const sessionRef = doc(db, 'learning_sessions', editingSession.id);
        await updateDoc(sessionRef, sessionData);
      } else {
        // Create new session
        await addDoc(collection(db, 'learning_sessions'), {
          ...sessionData,
          createdAt: new Date().toISOString()
        });
      }
      
      setShowModal(false);
      resetForm();
      fetchSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      if (error.code === 'permission-denied') {
        setError('Permission denied. You do not have permission to save sessions.');
      } else {
        setError(`Error saving session: ${error.message}`);
      }
    }
  };

  // Delete session
  const handleDeleteSession = async (sessionId) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        setError('');
        await deleteDoc(doc(db, 'learning_sessions', sessionId));
        fetchSessions();
      } catch (error) {
        console.error('Error deleting session:', error);
        if (error.code === 'permission-denied') {
          setError('Permission denied. You do not have permission to delete sessions.');
        } else {
          setError(`Error deleting session: ${error.message}`);
        }
      }
    }
  };

  // Filter sessions based on search term
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = 
      session.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.chatId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.lesson_plan?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Format date for display

  // Format time from minutes
  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Learning Sessions Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage all learning sessions including lesson plans, time tracking, attention monitoring, and cost analysis.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={handleCreateSession}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          >
            <FaPlus className="mr-2 h-4 w-4" />
            Add Session
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    onClick={() => setError('')}
                    className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="text-sm text-gray-700">
          {filteredSessions.length} of {sessions.length} sessions
        </div>
      </div>

      {/* Sessions Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <FaExclamationTriangle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Unable to load sessions</h3>
                  <p className="mt-1 text-sm text-gray-500">{error}</p>
                  <div className="mt-6">
                    <button
                      onClick={fetchSessions}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Session Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User/Chat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time & Attention
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cost
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSessions.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-green-300 flex items-center justify-center">
                                <FaGraduationCap className="h-5 w-5 text-green-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                Session {session.id.slice(-8)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {session.lesson_plan ? session.lesson_plan.substring(0, 50) + '...' : 'No lesson plan'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center">
                              <FaUser className="h-4 w-4 text-gray-400 mr-1" />
                              {session.userId || 'N/A'}
                            </div>
                            <div className="flex items-center mt-1">
                              <FaEye className="h-4 w-4 text-gray-400 mr-1" />
                              {session.chatId || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center">
                              <FaClock className="h-4 w-4 text-gray-400 mr-1" />
                              {formatTime(session.time_minutes)}
                            </div>
                            <div className="mt-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                session.attention_percentage >= 80 ? 'bg-green-100 text-green-800' :
                                session.attention_percentage >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {session.attention_percentage?.toFixed(1) || 0}% attention
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center">
                              <FaDollarSign className="h-4 w-4 text-gray-400 mr-1" />
                              ${session.cost_usd?.toFixed(2) || '0.00'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ${session.cost_per_minute_usd?.toFixed(2) || '0.00'}/min
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(session.createdAt, 'short')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditSession(session)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <FaEdit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSession(session.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {!loading && !error && filteredSessions.length === 0 && (
                <div className="text-center py-12">
                  <FaGraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions found</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating a new learning session.</p>
                  <div className="mt-6">
                    <button
                      onClick={handleCreateSession}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <FaPlus className="mr-2 h-4 w-4" />
                      Add Session
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSession ? 'Edit Session' : 'Add New Session'}
              </h3>
              <form onSubmit={handleSaveSession} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">User ID</label>
                  <input
                    type="text"
                    name="userId"
                    value={formData.userId}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Chat ID</label>
                  <input
                    type="text"
                    name="chatId"
                    value={formData.chatId}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Lesson Plan</label>
                  <textarea
                    name="lesson_plan"
                    value={formData.lesson_plan}
                    onChange={handleInputChange}
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Summary</label>
                  <textarea
                    name="summary"
                    value={formData.summary}
                    onChange={handleInputChange}
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Time (minutes)</label>
                    <input
                      type="number"
                      name="time_minutes"
                      value={formData.time_minutes}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Attention (%)</label>
                    <input
                      type="number"
                      name="attention_percentage"
                      value={formData.attention_percentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.1"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cost (USD)</label>
                    <input
                      type="number"
                      name="cost_usd"
                      value={formData.cost_usd}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cost per Minute (USD)</label>
                    <input
                      type="number"
                      name="cost_per_minute_usd"
                      value={formData.cost_per_minute_usd}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingSession ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningSessionManagement; 